import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  MdSearch,
  MdChatBubbleOutline,
  MdCircle,
} from "react-icons/md";

import API from "../../../services/api";
import getImageUrl from "../../../utils/imageUrl";
import "./ConversationList.css";


function ConversationList({
  selectedConversation,
  onSelectConversation,

  // =========================================================
  // MODE
  // =========================================================

  mode = "customer",

  // =========================================================
  // CONVERSATIONS FROM PARENT
  // =========================================================

  conversations: externalConversations,
}) {


  // =========================================================
  // STATE
  // =========================================================

  const [
    customerConversations,
    setCustomerConversations,
  ] = useState([]);


  const [
    search,
    setSearch,
  ] = useState("");


  const [
    loading,
    setLoading,
  ] = useState(
    mode === "customer" &&
    externalConversations === undefined
  );


  const [
    error,
    setError,
  ] = useState("");


  // =========================================================
  // MODE
  // =========================================================

  const isProvider =
    mode === "provider";


  // =========================================================
  // CONVERSATIONS
  // =========================================================

  const conversations =
    externalConversations !== undefined
      ? externalConversations
      : customerConversations;


  // =========================================================
  // LOAD CUSTOMER CONVERSATIONS
  // =========================================================

  const loadCustomerConversations =
    useCallback(
      async () => {

        if (isProvider) {
          return;
        }


        try {

          setLoading(true);

          setError("");


          const response =
            await API.get(
              "/conversations/customer"
            );


          if (
            response.data?.success
          ) {

            setCustomerConversations(
              response.data.conversations || []
            );

          } else {

            setCustomerConversations([]);

          }


        } catch (err) {

          console.error(
            "Load Customer Conversations Error:",
            err
          );


          setError(
            err.response?.data?.message ||
            "Failed to load conversations"
          );


        } finally {

          setLoading(false);

        }

      },
      [isProvider]
    );


  // =========================================================
  // CUSTOMER INITIAL LOAD
  // =========================================================

  useEffect(() => {

    if (
      !isProvider &&
      externalConversations === undefined
    ) {

      loadCustomerConversations();

    }

  }, [
    isProvider,
    externalConversations,
    loadCustomerConversations,
  ]);


  // =========================================================
  // SEARCH
  // =========================================================

  const filteredConversations =
    useMemo(
      () => {

        const searchText =
          search
            .toLowerCase()
            .trim();


        if (!searchText) {
          return conversations;
        }


        return conversations.filter(
          (conversation) => {


            // ================================================
            // PROVIDER MODE
            // ================================================

            if (isProvider) {

              const customer =
                conversation.customerId;


              const customerName =
                customer?.name ||
                customer?.fullName ||
                customer?.username ||
                customer?.displayName ||
                "";


              const customerEmail =
                customer?.email ||
                "";


              const customerPhone =
                customer?.phone ||
                "";


              const lastMessage =
                conversation.lastMessage ||
                "";


              return (

                customerName
                  .toLowerCase()
                  .includes(searchText) ||

                customerEmail
                  .toLowerCase()
                  .includes(searchText) ||

                customerPhone
                  .toLowerCase()
                  .includes(searchText) ||

                lastMessage
                  .toLowerCase()
                  .includes(searchText)

              );

            }


            // ================================================
            // CUSTOMER MODE
            // ================================================

            const provider =
              conversation.providerId;


            const businessName =
              provider?.business?.businessName ||
              provider?.business?.name ||
              provider?.businessName ||
              provider?.ownerName ||
              "";


            const providerType =
              provider?.providerType ||
              "";


            const lastMessage =
              conversation.lastMessage ||
              "";


            return (

              businessName
                .toLowerCase()
                .includes(searchText) ||

              providerType
                .toLowerCase()
                .includes(searchText) ||

              lastMessage
                .toLowerCase()
                .includes(searchText)

            );

          }
        );

      },
      [
        conversations,
        search,
        isProvider,
      ]
    );


  // =========================================================
  // PROVIDER IMAGE
  //
  // Return null when there is no image.
  // This allows the initial letter fallback.
  // =========================================================

const getProviderImage = (provider) => {

  return (
    getImageUrl(
      provider?.photos?.businessLogo ||
      provider?.businessLogo ||
      provider?.photos?.profilePhoto ||
      provider?.profilePhoto ||
      provider?.avatar
    ) || null
  );

};


  // =========================================================
  // CUSTOMER IMAGE
  //
  // Return null when there is no image.
  // =========================================================
const getCustomerImage = (customer) => {

  return (
    getImageUrl(
      customer?.profilePhoto ||
      customer?.photos?.profilePhoto ||
      customer?.avatar
    ) || null
  );

};

  // =========================================================
  // BUSINESS NAME
  // =========================================================

  const getBusinessName =
    (provider) => {

      return (
        provider?.business?.businessName ||
        provider?.business?.name ||
        provider?.businessName ||
        provider?.ownerName ||
        "HomeFoods Provider"
      );

    };


  // =========================================================
  // CUSTOMER NAME
  // =========================================================

  const getCustomerName =
    (customer) => {

      return (
        customer?.name ||
        customer?.fullName ||
        customer?.username ||
        customer?.displayName ||
        "HomeFoods Customer"
      );

    };


  // =========================================================
  // GET INITIAL
  //
  // Chandru Homefoods → C
  // Nithish Nadig → N
  // Rithesh Bakery → R
  // =========================================================

  const getInitial =
    (name) => {

      return (
        name
          ?.trim()
          ?.charAt(0)
          ?.toUpperCase() ||
        "U"
      );

    };


  // =========================================================
  // ONLINE STATUS
  // =========================================================

  const isUserOnline =
    (user) => {

      if (!user) {
        return false;
      }


      return (
        user?.isOnline === true ||
        user?.online === true ||
        user?.status === "online" ||
        user?.businessStatus === "Open"
      );

    };


  // =========================================================
  // FORMAT TIME
  // =========================================================

  const formatTime =
    (date) => {

      if (!date) {
        return "";
      }


      const messageDate =
        new Date(date);


      if (
        Number.isNaN(
          messageDate.getTime()
        )
      ) {

        return "";

      }


      const now =
        new Date();


      const sameDay =
        messageDate.toDateString() ===
        now.toDateString();


      if (sameDay) {

        return messageDate.toLocaleTimeString(
          [],
          {
            hour: "2-digit",
            minute: "2-digit",
          }
        );

      }


      return messageDate.toLocaleDateString(
        [],
        {
          day: "2-digit",
          month: "short",
        }
      );

    };


  // =========================================================
  // IMAGE ERROR HANDLER
  //
  // If image URL is broken:
  // hide image
  // show initial
  // =========================================================

  const handleImageError =
    (event) => {

      event.currentTarget.style.display =
        "none";


      const fallback =
        event.currentTarget.parentElement
          ?.querySelector(
            ".conversation-avatar-fallback"
          );


      if (fallback) {

        fallback.style.display =
          "flex";

      }

    };


  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {

    return (

      <aside className="conversation-list">

        <div className="conversation-list-header">

          <div className="conversation-title">

            <div className="conversation-title-icon">

              <MdChatBubbleOutline />

            </div>

            <div>

              <h2>
                Messages
              </h2>

              <span>
                Loading conversations...
              </span>

            </div>

          </div>

        </div>


        <div className="conversation-loading">

          <div className="conversation-loading-item" />

          <div className="conversation-loading-item" />

          <div className="conversation-loading-item" />

          <div className="conversation-loading-item" />

        </div>

      </aside>

    );

  }


  // =========================================================
  // HEADER TEXT
  // =========================================================

  const title =
    isProvider
      ? "Customer Messages"
      : "Messages";


  const conversationLabel =
    conversations.length === 1
      ? "conversation"
      : "conversations";


  // =========================================================
  // RENDER
  // =========================================================

  return (

    <aside className="conversation-list">


      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="conversation-list-header">

        <div className="conversation-title">

          <div className="conversation-title-icon">

            <MdChatBubbleOutline />

          </div>


          <div>

            <h2>
              {title}
            </h2>


            <span>

              {conversations.length}{" "}

              {conversationLabel}

            </span>

          </div>

        </div>

      </div>


      {/* =====================================================
          SEARCH
      ===================================================== */}

      <div className="conversation-search">

        <MdSearch />

        <input
          type="text"
          placeholder={
            isProvider
              ? "Search customers..."
              : "Search conversations..."
          }
          value={search}
          onChange={(event) =>
            setSearch(
              event.target.value
            )
          }
        />

      </div>


      {/* =====================================================
          ERROR
      ===================================================== */}

      {error && (

        <div className="conversation-error">

          <div>
            {error}
          </div>


          <button
            type="button"
            onClick={
              loadCustomerConversations
            }
          >
            Retry
          </button>

        </div>

      )}


      {/* =====================================================
          EMPTY
      ===================================================== */}

      {!error &&
        filteredConversations.length === 0 && (

          <div className="conversation-empty">

            <div className="conversation-empty-icon">

              <MdChatBubbleOutline />

            </div>


            <h3>

              {search
                ? "No conversations found"
                : isProvider
                ? "No customer conversations"
                : "No conversations yet"}

            </h3>


            <p>

              {search
                ? "Try searching with another name or message."
                : isProvider
                ? "Customer conversations will appear here."
                : "Start chatting with a provider to see your conversations here."}

            </p>

          </div>

        )}


      {/* =====================================================
          CONVERSATIONS
      ===================================================== */}

      {filteredConversations.length > 0 && (

        <div className="conversation-items">

          {filteredConversations.map(
            (conversation) => {


              // =================================================
              // PROVIDER MODE
              //
              // Provider sees CUSTOMER
              // =================================================

              if (isProvider) {

                const customer =
                  conversation.customerId;


                const customerName =
                  getCustomerName(
                    customer
                  );


                const customerImage =
                  getCustomerImage(
                    customer
                  );


                const isSelected =
                  selectedConversation?._id ===
                  conversation._id;


                const unreadCount =
                  conversation.unreadProvider ||
                  0;


                const isOnline =
                  isUserOnline(
                    customer
                  );


                const customerInitial =
                  getInitial(
                    customerName
                  );


                return (

                  <button
                    type="button"
                    key={
                      conversation._id
                    }
                    className={`conversation-item ${
                      isSelected
                        ? "selected"
                        : ""
                    }`}
                    onClick={() =>
                      onSelectConversation(
                        conversation
                      )
                    }
                  >


                    {/* =====================================
                        CUSTOMER AVATAR
                    ===================================== */}

                    <div className="conversation-avatar">

                      {customerImage && (

                        <img
                          src={
                            customerImage
                          }
                          alt={
                            customerName
                          }
                          onError={
                            handleImageError
                          }
                        />

                      )}


                      {/* INITIAL */}

                      <div
                        className="conversation-avatar-fallback"
                        style={{
                          display:
                            customerImage
                              ? "none"
                              : "flex",
                        }}
                      >

                        {customerInitial}

                      </div>


                      {/* ONLINE */}

                      {isOnline && (

                        <span className="online-dot">

                          <MdCircle />

                        </span>

                      )}

                    </div>


                    {/* =====================================
                        CUSTOMER DETAILS
                    ===================================== */}

                    <div className="conversation-details">


                      <div className="conversation-top-row">

                        <h3>
                          {customerName}
                        </h3>


                        <span className="conversation-time">

                          {formatTime(
                            conversation.lastMessageAt ||
                            conversation.updatedAt
                          )}

                        </span>

                      </div>


                      <div className="conversation-bottom-row">

                        <p
                          className={
                            unreadCount > 0
                              ? "unread"
                              : ""
                          }
                        >

                          {conversation.lastMessage ||
                            "Start a conversation"}

                        </p>


                        {unreadCount > 0 && (

                          <span className="unread-count">

                            {unreadCount > 99
                              ? "99+"
                              : unreadCount}

                          </span>

                        )}

                      </div>


                      <span className="conversation-provider-type">

                        Customer

                      </span>

                    </div>

                  </button>

                );

              }


              // =================================================
              // CUSTOMER MODE
              //
              // Customer sees PROVIDER
              // =================================================

              const provider =
                conversation.providerId;


              const businessName =
                getBusinessName(
                  provider
                );


              const providerImage =
                getProviderImage(
                  provider
                );


              const isSelected =
                selectedConversation?._id ===
                conversation._id;


              const unreadCount =
                conversation.unreadCustomer ||
                0;


              const isOnline =
                isUserOnline(
                  provider
                );


              const providerInitial =
                getInitial(
                  businessName
                );


              return (

                <button
                  type="button"
                  key={
                    conversation._id
                  }
                  className={`conversation-item ${
                    isSelected
                      ? "selected"
                      : ""
                  }`}
                  onClick={() =>
                    onSelectConversation(
                      conversation
                    )
                  }
                >


                  {/* =====================================
                      PROVIDER AVATAR
                  ===================================== */}

                  <div className="conversation-avatar">

                    {providerImage && (

                      <img
                        src={
                          providerImage
                        }
                        alt={
                          businessName
                        }
                        onError={
                          handleImageError
                        }
                      />

                    )}


                    {/* INITIAL */}

                    <div
                      className="conversation-avatar-fallback"
                      style={{
                        display:
                          providerImage
                            ? "none"
                            : "flex",
                      }}
                    >

                      {providerInitial}

                    </div>


                    {/* ONLINE */}

                    {isOnline && (

                      <span className="online-dot">

                        <MdCircle />

                      </span>

                    )}

                  </div>


                  {/* =====================================
                      PROVIDER DETAILS
                  ===================================== */}

                  <div className="conversation-details">


                    <div className="conversation-top-row">

                      <h3>
                        {businessName}
                      </h3>


                      <span className="conversation-time">

                        {formatTime(
                          conversation.lastMessageAt ||
                          conversation.updatedAt
                        )}

                      </span>

                    </div>


                    <div className="conversation-bottom-row">

                      <p
                        className={
                          unreadCount > 0
                            ? "unread"
                            : ""
                        }
                      >

                        {conversation.lastMessage ||
                          "Start a conversation"}

                      </p>


                      {unreadCount > 0 && (

                        <span className="unread-count">

                          {unreadCount > 99
                            ? "99+"
                            : unreadCount}

                        </span>

                      )}

                    </div>


                    <span className="conversation-provider-type">

                      {provider?.providerType ||
                        "Food Provider"}

                    </span>

                  </div>

                </button>

              );

            }
          )}

        </div>

      )}

    </aside>

  );

}


export default ConversationList;